/**
 * Requirement creation form options - synchronized with BVP BasicParam.tsx
 */

import type { Translate } from '@/i18n/translate';

/** Translated copy of an options array; `value` stays untouched. */
export const localizeOptions = <T extends { label: string }>(
  options: T[],
  t: Translate
): T[] => options.map((option) => ({ ...option, label: t(option.label) }));

// Priority options
export const PRIORITY_OPTIONS = [
  { value: '必选', label: '必选' },
  { value: '可选', label: '可选' },
];

// MCU tag options
export const MCU_TAG_OPTIONS = [
  { value: 'OneMcu', label: 'OneMcu' },
  { value: 'TwoMcu', label: 'TwoMcu' },
  { value: 'TC397', label: 'TC397' },
  { value: 'U2A8', label: 'U2A8' },
  { value: 'U2A16', label: 'U2A16' },
  { value: 'VectorCP', label: 'Vector CP' },
  { value: 'EtasCP', label: 'Etas CP' },
  { value: 'HuaweiCP', label: 'Huawei CP' },
  { value: 'L3', label: 'L3' },
  { value: 'L4', label: 'L4' },
];

// SOC tag options
export const SOC_TAG_OPTIONS = [
  { value: 'OneSoc', label: 'OneSoc' },
  { value: 'TwoSoc', label: 'TwoSoc' },
  { value: 'Qnx', label: 'Qnx' },
  { value: 'Linux', label: 'Linux' },
  { value: 'StandardDriveOs', label: 'Standard DriveOs' },
  { value: 'SafetyDriveOs', label: 'Safety DriveOs' },
  { value: 'Qualcomm', label: 'Qualcomm' },
  { value: 'HuaweiMdc', label: 'Huawei MDC' },
  { value: 'BMC', label: 'BMC' },
  { value: 'VectorAP', label: 'Vector AP' },
  { value: 'EtasAP', label: 'Etas AP' },
  { value: 'OBFAP', label: 'OBF AP' },
  { value: '自研AP', label: '自研 AP' },
  { value: 'L3', label: 'L3' },
  { value: 'L4', label: 'L4' },
];

// Category options by subcategory (synchronized with BVP BasicParam.tsx)
export const CATEGORY_OPTIONS_BY_SUBCATEGORY: Record<string, Array<{ value: string; label: string }>> = {
  Mcu: [
    { value: '通用', label: '通用' },
    { value: 'SomeIp', label: 'SomeIp' },
    { value: 'CddMlog', label: 'CddMlog' },
    { value: 'BEM_CP', label: 'BEM_CP' },
    { value: 'CFS_CP', label: 'CFS_CP' },
    { value: 'DGW_CP', label: 'DGW_CP' },
    { value: '日志', label: '日志' },
    { value: 'COM', label: 'COM' },
    { value: 'Debug', label: 'Debug' },
    { value: 'OBF_CddMlog', label: 'OBF_CddMlog' },
  ],
  Communication: [
    { value: '通用', label: '通用' },
    { value: 'ETH', label: 'ETH' },
    { value: 'SomeIp', label: 'SomeIp' },
    { value: 'CAN', label: 'CAN' },
    { value: 'LIN', label: 'LIN' },
  ],
  Sensor: [
    { value: '通用', label: '通用' },
    { value: 'Camera', label: 'Camera' },
    { value: 'CameraDriver', label: 'CameraDriver' },
    { value: 'DMSOMSCamera', label: 'DMSOMSCamera' },
    { value: 'L3/L4_Camera', label: 'L3/L4_Camera' },
    { value: 'Lidar', label: 'Lidar' },
    { value: 'Radar', label: 'Radar' },
    { value: 'USS', label: 'USS' },
    { value: 'IMU', label: 'IMU' },
    { value: 'GNSS', label: 'GNSS' },
  ],
  SocAndMiddleware: [
    { value: '通用', label: '通用' },
    { value: 'BSP', label: 'BSP' },
    { value: 'OS', label: 'OS' },
    { value: '存储', label: '存储' },
    { value: 'SM', label: 'SM' },
    { value: 'EM', label: 'EM' },
    { value: 'PHM', label: 'PHM' },
  ],
  DiagAndFault: [
    { value: '通用', label: '通用' },
    { value: 'State Service', label: 'State Service' },
  ],
  TimeSync: [
    { value: '通用', label: '通用' },
    { value: 'OT', label: 'OT' },
    { value: 'BSP', label: 'BSP' },
    { value: 'TM_CP', label: 'TM_CP' },
    { value: 'IMU', label: 'IMU' },
    { value: 'GNSS', label: 'GNSS' },
    { value: 'Lidar', label: 'Lidar' },
    { value: 'WT', label: 'WT' },
    { value: 'Radar', label: 'Radar' },
  ],
  UpgradeAndPower: [
    { value: '通用', label: '通用' },
    { value: '上下电', label: '上下电' },
    { value: 'OTA', label: 'OTA' },
    { value: '软件刷写', label: '软件刷写' },
    { value: 'State_Service', label: 'State_Service' },
  ],
  FunctionalSafety: [
    { value: '通用', label: '通用' },
    { value: '热管理', label: '热管理' },
    { value: 'SOC&MCU', label: 'SOC&MCU' },
    { value: 'MCU', label: 'MCU' },
    { value: '存储器', label: '存储器' },
    { value: '通讯', label: '通讯' },
    { value: 'FFI', label: 'FFI' },
    { value: '看门狗', label: '看门狗' },
    { value: '电源管理', label: '电源管理' },
  ],
  CyberSecurity: [
    { value: '通用', label: '通用' },
    { value: 'IDPS', label: 'IDPS' },
    { value: '加密技术', label: '加密技术' },
    { value: '密钥管理', label: '密钥管理' },
    { value: '防火墙', label: '防火墙' },
    { value: '安全诊断', label: '安全诊断' },
    { value: '安全通讯', label: '安全通讯' },
    { value: '安全刷写', label: '安全刷写' },
    { value: '安全启动', label: '安全启动' },
    { value: '操作系统安全', label: '操作系统安全' },
    { value: '软件加固', label: '软件加固' },
    { value: '安全调试', label: '安全调试' },
    { value: '漏洞管理', label: '漏洞管理' },
    { value: '安全存储', label: '安全存储' },
    { value: '安全日志', label: '安全日志' },
    { value: '安全访问', label: '安全访问' },
  ],
  Other: [
    { value: '通用', label: '通用' },
    { value: '集成', label: '集成' },
    { value: '调试', label: '调试' },
    { value: '产线离线刷写', label: '产线离线刷写' },
    { value: '存储管理', label: '存储管理' },
  ],
};

// Helper function to get category options by subcategory
export const getCategoryOptions = (subcategory: string) => {
  return CATEGORY_OPTIONS_BY_SUBCATEGORY[subcategory as keyof typeof CATEGORY_OPTIONS_BY_SUBCATEGORY] || [];
};
